import React, { useState } from 'react';
import { getPlayModeLabel } from '../App';
import {
	computeActivePlayModeScopeStats,
	computeAlternateWindowSummaries,
	formatScopeStatsLine,
	formatWindowSummaryLine,
} from '../lib/playWindowStats';
import { useJurisdictionsCatalog } from '../lib/jurisdictionsCatalog';

/**
 * Progress box for map bar and mobile header — stats follow active play mode.
 */
const GameProgressDisplay = ({
	mapType,
	playMode,
	visitedStates,
	stateTiers,
	stateSightings,
	currentMonthYear,
	windowRegionUnions,
	variant = 'desktop',
}) => {
	const [showAlternates, setShowAlternates] = useState(false);
	const { denominators, scopeOptions } = useJurisdictionsCatalog();

	const baseStats = computeActivePlayModeScopeStats({
		mapType,
		playMode,
		visitedStates,
		stateTiers,
		stateSightings,
		currentMonthYear,
		denominators,
	});
	const scopeLabel = scopeOptions?.find((o) => o.code === baseStats.scope)?.name;
	const activeStats = scopeLabel ? { ...baseStats, label: scopeLabel } : baseStats;

	const alternates = computeAlternateWindowSummaries({
		mapType,
		playMode,
		visitedStates,
		stateTiers,
		stateSightings,
		currentMonthYear,
		windowRegionUnions,
		denominators,
	});

	const progressBarWidth = Math.min(100, activeStats.percentNumber);
	const mainLine = formatScopeStatsLine(activeStats);
	const hasAlternates = alternates.length > 0;

  if (variant === 'mobile') {
    return (
      <>
        <div className="progress-header">
          <span className="play-mode-indicator" aria-label={`Play mode: ${getPlayModeLabel(playMode)}`}>
            {getPlayModeLabel(playMode)}
          </span>
          <div className="progress-header__main">
            <h2>{mainLine}</h2>
            {hasAlternates && (
              <button
                type="button"
                className="progress-alternates-toggle"
                onClick={() => setShowAlternates((v) => !v)}
                aria-expanded={showAlternates}
              >
                {showAlternates ? 'Hide' : 'Other periods'}
              </button>
            )}
          </div>
        </div>
        <div className="mobile-progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progressBarWidth}%` }}
            aria-valuenow={progressBarWidth}
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>
        {showAlternates && hasAlternates && (
          <ul className="progress-alternates-list">
            {alternates.map((summary) => (
              <li key={summary.window}>{formatWindowSummaryLine(summary)}</li>
            ))}
          </ul>
        )}
      </>
    );
  }

  return (
    <div className="progress game-progress-display">
      <span className="play-mode-indicator" aria-label={`Play mode: ${getPlayModeLabel(playMode)}`}>
        {getPlayModeLabel(playMode)}
      </span>
      <span className="progress__main">{mainLine}</span>
			{hasAlternates && (
				<button
					type="button"
					className="progress-alternates-toggle progress-alternates-toggle--compact"
					onClick={() => setShowAlternates((v) => !v)}
					aria-expanded={showAlternates}
					title="Show stats for other time periods"
				>
					{showAlternates ? '▲' : '▼'}
				</button>
			)}
			{showAlternates && hasAlternates && (
				<ul className="progress-alternates-list progress-alternates-list--desktop">
					{alternates.map((summary) => (
						<li key={summary.window}>{formatWindowSummaryLine(summary)}</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default GameProgressDisplay;
